/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import React, { useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import { DATE, formatDate } from '../../utils/date';
import { CurrentUserData } from '../../reducers/current-user';
import SimpleTooltip from '../simple-tooltip';
import G17Client from '../../services/G17Client';
import { useAppDispatch } from '../../reducers';
import { addSiteAlert } from '../../slice/siteAlertsSlice';
import { UtrvComment } from '../../types/universalTrackerValue';
import { Mention, MentionsInput, SuggestionDataItem } from 'react-mentions';
import './styles.scss';

interface CommentProps {
  users: SuggestionDataItem[];
  comment: UtrvComment;
  utrvId: string;
  user: CurrentUserData;
  handleReload: () => void;
  isScrolledTo: boolean;
}

const Comment = ({ users, comment, utrvId, user, handleReload, isScrolledTo }: CommentProps) => {
  const commentRef = useRef<null |HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const handleDelete = () => {
    if (!window.confirm('Do you really want to delete this comment? This action cannot be undone.')) {
      return;
    }
    G17Client
      .deleteUtrvComment(utrvId, comment._id)
      .then(() => handleReload())
      .catch(() => {
        dispatch(addSiteAlert({
          content: 'There was a problem deleting your comment. Please refresh and try again.'
        }));
      })
  }
  const isOwner = user._id === comment.user._id;

  const deleteButton = isOwner ? (
    <div>
      <SimpleTooltip text='Delete this comment. This action cannot be undone.'>
        <Button size='sm' color='link' className='delete-btn p-0 float-end' onClick={handleDelete}>
          <i className='fa fa-trash' />
        </Button>
      </SimpleTooltip>
    </div>
  ) : null;

  const scrollToRef = () => {
    if (commentRef.current) {
      commentRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }

  useEffect(() => {
    if (isScrolledTo) {
      scrollToRef();
    }
  }, [isScrolledTo])

  return (
      <div ref={commentRef} className={isScrolledTo ? 'card mb-2 active' : 'card mb-2'}>
        <div className='card-body'>
          <div className='card-title d-flex'>
            <div className='flex-fill strong'>{comment.user.name}</div>
            {deleteButton}
          </div>
          <p className='card-subtitle'>{formatDate(comment.created, DATE.HUMANIZE)}</p>
          <div className='card-text'>
            <MentionsInput
              className='mentions-input mentions-input-disabled'
              value={comment.text}
              disabled={true}>
              <Mention
                trigger='@'
                data={users}
                className='mention'
                displayTransform={(id, display) => ` @${display} `}
                appendSpaceOnAdd={true}
              />
            </MentionsInput>
          </div>
        </div>
      </div>
  );
}

export default Comment;
